/**
 * POST /api/checkout/stripe-return — what became of a payment that left the
 * page, asked of Stripe rather than read from the return URL.
 * See utils/checkout/stripe-return.ts.
 */
import { z } from 'zod';
import { TaxEstimateSchema } from './tax-estimate';

export const StripeReturnRequestSchema = z.object({
  paymentIntentId: z.string().regex(/^pi_[A-Za-z0-9]+$/),
  clientSecret: z.string().min(10).max(255),
  listingId: z.coerce.number().int().positive(),
});

export type StripeReturnRequest = z.infer<typeof StripeReturnRequestSchema>;

export const StripeReturnSuccessResponseSchema = z.object({
  success: z.literal(true),
  outcome: z.enum(['paid', 'processing', 'failed']),
  /** Stripe's payment method type (`cashapp`, `card`), for the complete event. */
  paymentMethodType: z.string(),
  receipt: z.object({
    rate: z.object({
      id: z.string(),
      amount: z.number().min(0),
      service: z.string(),
      provider: z.object({ name: z.string(), image: z.string().nullable() }),
      estimatedDays: z.number(),
      serviceToken: z.string().optional(),
      tax: TaxEstimateSchema.optional(),
    }),
    email: z.string(),
    coupon: z.object({ discount: z.number(), code: z.string(), mode: z.enum(['code', 'auto']) }).nullable(),
  }).nullable(),
});

export const StripeReturnErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export const StripeReturnResponseSchema = z.discriminatedUnion('success', [
  StripeReturnSuccessResponseSchema,
  StripeReturnErrorResponseSchema,
]);

export type StripeReturnResponse = z.infer<typeof StripeReturnResponseSchema>;
